import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users,
  ArrowLeft,
  Loader2,
  Search,
  Building2,
  Mail,
  Shield,
  UserCheck,
  Crown,
  Filter,
  AlertTriangle,
  Link2,
  Edit,
  Trash2
} from 'lucide-react';
import Layout from '../../components/Layout';
import api from '../../lib/api';

const UsersPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompanyForAssign, setSelectedCompanyForAssign] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [bulkAssignCompany, setBulkAssignCompany] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
     
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch users first
      const usersRes = await api.get('/admin/all-users');
      setUsers(usersRes.data || []);
      
      // Fetch companies
      try {
        const companiesRes = await api.get('/admin/companies');
        setCompanies(companiesRes.data || []);
      } catch (compErr) {
        console.error('Failed to fetch companies:', compErr);
        setCompanies([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Redirect non-super-admins
  if (user?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-red-400">Access denied. Super Admin only.</p>
        </div>
      </Layout>
    );
  }

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    let matchesCompany = true;
    if (companyFilter === 'orphaned') {
      matchesCompany = !u.company_id && u.role !== 'super_admin';
    } else if (companyFilter !== 'all') {
      matchesCompany = u.company_id === companyFilter;
    }
    return matchesSearch && matchesRole && matchesCompany;
  });

  // Get company name by ID
  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'No Company';
  };

  // Count by role
  const roleCount = {
    admin: users.filter(u => u.role === 'admin').length,
    supervisor: users.filter(u => u.role === 'supervisor').length,
    technician: users.filter(u => u.role === 'technician').length,
    super_admin: users.filter(u => u.role === 'super_admin').length,
  };

  // Count orphaned users (no company)
  const orphanedCount = users.filter(u => !u.company_id && u.role !== 'super_admin').length;

  const handleAssignUser = async () => {
    if (!selectedUser || !selectedCompanyForAssign) {
      toast.error('Please select a company');
      return;
    }
    try {
      setAssigning(true);
      await api.post(`/admin/assign-user/${selectedUser.id}/to/${selectedCompanyForAssign}`);
      toast.success('User assigned to company');
      setAssignDialogOpen(false);
      setSelectedUser(null);
      setSelectedCompanyForAssign('');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign user');
    } finally {
      setAssigning(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignCompany) {
      toast.error('Please select a company');
      return;
    }
    try {
      setBulkAssigning(true);
      const response = await api.post(`/admin/assign-orphaned-users/${bulkAssignCompany}`);
      toast.success(response.data.message);
      setBulkAssignDialogOpen(false);
      setBulkAssignCompany('');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign users');
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'technician',
      company_id: user.company_id || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    try {
      setSaving(true);
      await api.put(`/admin/users/${editingUser.id}`, editFormData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setEditingUser(null);
      setEditFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = (user) => {
    setDeletingUser(user);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!deletingUser) return;
    
    try {
      setDeleting(true);
      await api.delete(`/admin/users/${deletingUser.id}`);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-tight text-white flex items-center gap-2">
                <Users className="w-8 h-8 text-cyan-500" />
                All Platform Users
              </h1>
              <p className="text-slate-400">View and manage users across all companies</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="card-industrial p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-cyan-500" />
              <div>
                <p className="text-slate-400 text-xs uppercase">Total Users</p>
                <p className="text-2xl font-bold text-white">{users.length}</p>
              </div>
            </div>
          </div>
          <div className="card-industrial p-4">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-slate-400 text-xs uppercase">Admins</p>
                <p className="text-2xl font-bold text-purple-400">{roleCount.admin}</p>
              </div>
            </div>
          </div>
          <div className="card-industrial p-4">
            <div className="flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-slate-400 text-xs uppercase">Supervisors</p>
                <p className="text-2xl font-bold text-amber-400">{roleCount.supervisor}</p>
              </div>
            </div>
          </div>
          <div className="card-industrial p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-slate-400 text-xs uppercase">Technicians</p>
                <p className="text-2xl font-bold text-green-400">{roleCount.technician}</p>
              </div>
            </div>
          </div>
          <div 
            className={`card-industrial p-4 ${orphanedCount > 0 ? 'border-red-500/50' : ''}`}
            onClick={() => orphanedCount > 0 && setCompanyFilter('orphaned')}
            style={{ cursor: orphanedCount > 0 ? 'pointer' : 'default' }}
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className={`w-8 h-8 ${orphanedCount > 0 ? 'text-red-500' : 'text-slate-500'}`} />
              <div>
                <p className="text-slate-400 text-xs uppercase">No Company</p>
                <p className={`text-2xl font-bold ${orphanedCount > 0 ? 'text-red-400' : 'text-slate-400'}`}>{orphanedCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Orphaned Users Warning */}
        {orphanedCount > 0 && (
          <div className="bg-red-900/20 border border-red-600/30 p-4 rounded flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-400" />
              <div>
                <p className="text-red-400 font-medium">{orphanedCount} users have no company assigned</p>
                <p className="text-slate-400 text-sm">These users cannot access company-specific data. Assign them to a company.</p>
              </div>
            </div>
            <Dialog open={bulkAssignDialogOpen} onOpenChange={setBulkAssignDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-600 hover:bg-red-700">
                  <Link2 className="w-4 h-4 mr-2" />
                  Assign All to Company
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-800 border-slate-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Assign Orphaned Users</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-slate-400">
                    This will assign all {orphanedCount} orphaned users to the selected company.
                  </p>
                  <select
                    value={bulkAssignCompany}
                    onChange={(e) => setBulkAssignCompany(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 text-white rounded px-3 py-2"
                  >
                    <option value="">Select a company...</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setBulkAssignDialogOpen(false)} className="btn-secondary">
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleBulkAssign} 
                      disabled={bulkAssigning || !bulkAssignCompany}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {bulkAssigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                      Assign {orphanedCount} Users
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Filters */}
        <div className="card-industrial p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-900 border-slate-600"
                />
              </div>
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="bg-slate-900 border border-slate-600 text-white rounded px-3 py-2"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="supervisor">Supervisor</option>
              <option value="technician">Technician</option>
            </select>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="bg-slate-900 border border-slate-600 text-white rounded px-3 py-2"
            >
              <option value="all">All Companies</option>
              <option value="orphaned">⚠️ No Company</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="card-industrial overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-900">
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">User</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">Email</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">Company</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">Role</th>
                  <th className="text-left py-3 px-4 text-slate-400 text-xs uppercase">Status</th>
                  <th className="text-right py-3 px-4 text-slate-400 text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-slate-700/50 hover:bg-slate-800/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                          {u.role === 'super_admin' ? (
                            <Crown className="w-4 h-4 text-purple-400" />
                          ) : (
                            <span className="text-white text-sm font-medium">
                              {u.name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <span className="text-white font-medium">{u.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-slate-300">{u.email}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className={`${!u.company_id && u.role !== 'super_admin' ? 'text-red-400' : 'text-slate-300'}`}>
                          {u.role === 'super_admin' ? 'Platform' : getCompanyName(u.company_id)}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded uppercase ${
                        u.role === 'super_admin' ? 'bg-purple-500/20 text-purple-400' :
                        u.role === 'admin' ? 'bg-blue-500/20 text-blue-400' :
                        u.role === 'supervisor' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {u.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-400">
                        Active
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Assign button for orphaned users */}
                        {!u.company_id && u.role !== 'super_admin' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(u);
                              setAssignDialogOpen(true);
                            }}
                            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                          >
                            <Link2 className="w-4 h-4 mr-1" />
                            Assign
                          </Button>
                        )}
                        
                        {/* Edit button for all users except super admin */}
                        {u.role !== 'super_admin' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditUser(u)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Delete button for all users except super admin */}
                        {u.role !== 'super_admin' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(u)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No users found matching your filters</p>
            </div>
          )}
        </div>

        {/* Individual Assign Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="bg-slate-800 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Assign User to Company</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {selectedUser && (
                <div className="bg-slate-900 p-3 rounded">
                  <p className="text-white font-medium">{selectedUser.name}</p>
                  <p className="text-slate-400 text-sm">{selectedUser.email}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-slate-400">Select Company</Label>
                <Select value={selectedCompanyForAssign} onValueChange={setSelectedCompanyForAssign}>
                  <SelectTrigger className="w-full bg-slate-900 border-slate-600 text-white">
                    <SelectValue placeholder="Select a company..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px] overflow-y-auto">
                    {companies.length > 0 ? (
                      companies.map(c => (
                        <SelectItem key={c.id} value={c.id} className="text-white cursor-pointer hover:bg-slate-800">
                          {c.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-3 text-slate-500 text-center text-sm">
                        No companies available
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setAssignDialogOpen(false)} className="btn-secondary">
                  Cancel
                </Button>
                <Button 
                  onClick={handleAssignUser} 
                  disabled={assigning || !selectedCompanyForAssign}
                  className="btn-primary"
                >
                  {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
                  Assign
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Edit User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-slate-400 text-sm block mb-2">Name</label>
                <Input
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                  placeholder="Full Name"
                />
              </div>
              
              <div>
                <label className="text-slate-400 text-sm block mb-2">Email</label>
                <Input
                  type="email"
                  value={editFormData.email || ''}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  className="bg-slate-900 border-slate-600 text-white"
                  placeholder="email@company.com"
                />
              </div>
              
              <div>
                <Label className="text-slate-400 mb-2 block">Role</Label>
                <Select 
                  value={editFormData.role || 'technician'} 
                  onValueChange={(value) => setEditFormData({...editFormData, role: value})}
                >
                  <SelectTrigger className="w-full bg-slate-900 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="admin" className="text-white cursor-pointer">Admin</SelectItem>
                    <SelectItem value="supervisor" className="text-white cursor-pointer">Supervisor</SelectItem>
                    <SelectItem value="technician" className="text-white cursor-pointer">Technician</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-slate-400 mb-2 block">Company</Label>
                <Select 
                  value={editFormData.company_id || 'none'} 
                  onValueChange={(value) => setEditFormData({...editFormData, company_id: value === 'none' ? '' : value})}
                >
                  <SelectTrigger className="w-full bg-slate-900 border-slate-600 text-white">
                    <SelectValue placeholder="No Company" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px] overflow-y-auto">
                    <SelectItem value="none" className="text-white cursor-pointer">No Company</SelectItem>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id} className="text-white cursor-pointer">
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setEditDialogOpen(false)} 
                  className="btn-secondary"
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveUser} 
                  disabled={saving || !editFormData.name || !editFormData.email}
                  className="btn-primary"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Delete User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {deletingUser && (
                <div className="bg-red-900/20 border border-red-500/30 rounded p-4">
                  <p className="text-white font-medium mb-2">Are you sure you want to delete this user?</p>
                  <div className="bg-slate-900 p-3 rounded mt-3">
                    <p className="text-white font-medium">{deletingUser.name}</p>
                    <p className="text-slate-400 text-sm">{deletingUser.email}</p>
                    <p className="text-slate-500 text-xs mt-1">Role: {deletingUser.role}</p>
                  </div>
                  <p className="text-red-400 text-sm mt-3">⚠️ This action cannot be undone.</p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setDeleteDialogOpen(false)} 
                  className="btn-secondary"
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmDeleteUser} 
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete User
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UsersPage;
